import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { formsApi, locationsApi, PHOTOS_BASE_URL } from '../../services/api';
import {
    FileText,
    Eye,
    X,
    ChevronLeft,
    ChevronRight,
    Calendar,
    MapPin,
    Building2,
    User,
    Star,
    Image,
    AlertCircle
} from 'lucide-react';

const FormSubmissionsPage = () => {
    const { selectedCorporate } = useOutletContext();
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [availableForms, setAvailableForms] = useState([]);
    const [selectedFormIdFilter, setSelectedFormIdFilter] = useState(null);
    const [availableLocations, setAvailableLocations] = useState([]);
    const [selectedLocationIdFilter, setSelectedLocationIdFilter] = useState(null);

    useEffect(() => {
        loadSubmissions();
        loadAvailableForms();
        loadAvailableLocations();
    }, [page, selectedCorporate, selectedFormIdFilter, selectedLocationIdFilter]);

    const loadAvailableForms = async () => {
        if (!selectedCorporate?.id) return;
        try {
            const data = await formsApi.getTemplates(selectedCorporate.id);
            setAvailableForms(data || []);
        } catch (err) {
            console.error('Form yükleme hatası:', err);
        }
    };

    const loadAvailableLocations = async () => {
        if (!selectedCorporate?.id) return;
        try {
            const data = await locationsApi.getAll(selectedCorporate.id);
            setAvailableLocations(data || []);
        } catch (err) {
            console.error('Lokasyon yükleme hatası:', err);
        }
    };

    const loadSubmissions = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await formsApi.getSubmissions(
                page,
                pageSize,
                selectedCorporate?.id,
                selectedLocationIdFilter,
                selectedFormIdFilter
            );
            setSubmissions(data.items || []);
            setTotalPages(data.totalPages || 0);
            setTotalCount(data.totalCount || 0);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetail = async (submission) => {
        setDetailLoading(true);
        setShowDetailModal(true);
        try {
            const detail = await formsApi.getSubmissionDetail(submission.id);
            setSelectedSubmission(detail);
        } catch (err) {
            alert('Detay yüklenemedi: ' + err.message);
            setShowDetailModal(false);
        } finally {
            setDetailLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getQuestionTypeName = (type) => {
        const types = { 1: 'Açık Metin', 2: 'Yıldız Puanlama', 3: 'Çoktan Seçmeli' };
        return types[type] || 'Bilinmiyor';
    };

    const renderAnswer = (answer) => {
        switch (answer.questionType) {
            case 1: // Açık Metin
                return (
                    <span className="text-gray-700">{answer.answerText}</span>
                );
            case 2: // Yıldız Puanlama
                return (
                    <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                                key={star}
                                className={`w-4 h-4 ${star <= (answer.ratingValue || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                            />
                        ))}
                        <span className="ml-2 text-sm text-gray-600">({answer.ratingValue || 0}/5)</span>
                    </div>
                );
            case 3: // Çoktan Seçmeli
                return <span className="font-medium">{answer.selectedOptionText || answer.answerText || '-'}</span>;
            default:
                return <span>{answer.answerText || '-'}</span>;
        }
    };

    const filteredSubmissions = submissions;

    if (loading && page === 1) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
            </div>
        );
    }

    return (
        <div>
            <div className="space-y-6">
                {/* Page Header */}
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Bildirimler</h1>
                    <p className="text-gray-500 mt-1">
                        Toplam {totalCount} bildirim bulundu
                    </p>
                </div>

                {/* Search and Filters */}
                <div className="bg-white rounded-xl shadow-sm border p-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {availableForms.length > 0 && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Forma göre filtrele
                                </label>
                                <select
                                    value={selectedFormIdFilter || ''}
                                    onChange={(e) => {
                                        const formId = e.target.value ? parseInt(e.target.value) : null;
                                        const formTitle = availableForms.find(f => f.id === formId)?.title || '';
                                        setSelectedFormIdFilter(formId);
                                        setPage(1);
                                    }}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Tüm Formlar</option>
                                    {availableForms.map((form) => (
                                        <option key={form.id} value={form.id}>
                                            {form.title}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {availableLocations.length > 0 && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Lokasyona göre filtrele
                                </label>
                                <select
                                    value={selectedLocationIdFilter || ''}
                                    onChange={(e) => {
                                        const locationId = e.target.value ? parseInt(e.target.value) : null;
                                        const locationName = availableLocations.find(l => l.id === locationId)?.name || '';
                                        setSelectedLocationIdFilter(locationId);
                                        setPage(1);
                                    }}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Tüm Lokasyonlar</option>
                                    {availableLocations.map((location) => (
                                        <option key={location.id} value={location.id}>
                                            {location.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-red-500" />
                        <p className="text-red-700">{error}</p>
                    </div>
                )}

                {/* Submissions Table */}
                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                    {filteredSubmissions.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                            <p>Bildirim bulunamadı.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Form
                                        </th>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Lokasyon
                                        </th>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Kurum
                                        </th>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Tarih
                                        </th>
                                        <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            İşlem
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredSubmissions.map((submission) => (
                                        <tr key={submission.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <p className="font-medium text-gray-900">{submission.formTitle}</p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2 text-gray-600">
                                                    <MapPin className="w-4 h-4 text-gray-400" />
                                                    {submission.locationName}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2 text-gray-600">
                                                    <Building2 className="w-4 h-4 text-gray-400" />
                                                    {submission.corporateName}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-gray-500 text-sm">
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="w-4 h-4 text-gray-400" />
                                                    {formatDate(submission.submittedAt)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-end">
                                                    <button
                                                        onClick={() => handleViewDetail(submission)}
                                                        className="inline-flex items-center gap-1 px-0 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                        Detay
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-6 py-4 border-t">
                            <p className="text-sm text-gray-500">
                                Sayfa {page} / {totalPages}
                            </p>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 1}
                                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => setPage(page + 1)}
                                    disabled={page === totalPages}
                                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Detail Modal */}
            {showDetailModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Bildirim Detayı
                            </h3>
                            <button
                                onClick={() => {
                                    setShowDetailModal(false);
                                    setSelectedSubmission(null);
                                }}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {detailLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
                                </div>
                            ) : selectedSubmission ? (
                                <div className="space-y-6">
                                    {/* Header Info */}
                                    <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                                        <div>
                                            <p className="text-sm text-gray-500">Form</p>
                                            <p className="font-medium text-gray-900">{selectedSubmission.formTemplate?.title}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Lokasyon</p>
                                            <p className="font-medium text-gray-900">{selectedSubmission.locationName}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Personel</p>
                                            <p className="font-medium text-gray-900">{selectedSubmission.userName || '-'}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Gönderim Tarihi</p>
                                            <p className="font-medium text-gray-900">{formatDate(selectedSubmission.submittedAt)}</p>
                                        </div>
                                    </div>

                                    {/* Answers */}
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-4">Cevaplar</h4>
                                        <div className="space-y-4">
                                            {selectedSubmission.answers?.map((answer, index) => (
                                                <div key={answer.id || index} className="border rounded-lg p-4">
                                                    <div className="flex items-start justify-between gap-4">
                                                        <div className="flex-1">
                                                            <p className="text-sm text-gray-500 mb-1">
                                                                Soru {index + 1} • {getQuestionTypeName(answer.questionType)}
                                                            </p>
                                                            <p className="font-medium text-gray-900 mb-2">{answer.questionText}</p>
                                                            <div className="mt-2">
                                                                {renderAnswer(answer)}
                                                            </div>
                                                        </div>
                                                        {answer.photoUrl && (
                                                            <a
                                                                href={`${PHOTOS_BASE_URL()}${answer.photoUrl}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="flex-shrink-0"
                                                            >
                                                                <img
                                                                    src={`${PHOTOS_BASE_URL()}${answer.photoUrl}`}
                                                                    alt="Submitted photo"
                                                                    className="w-20 h-20 object-cover rounded-lg border border-gray-200 hover:border-blue-400 transition-colors cursor-pointer"
                                                                />
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-center text-gray-500">Veri yüklenemedi.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FormSubmissionsPage;
